import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { StyledDropDown } from "../../layer01";
import { SmallCustomIcon } from "../../layer00";
import { CheckInHistoryItem } from "app/containers/TeamsTab/types";

export type SelectCheckInDateFieldProps = {
  checkInHistory?: CheckInHistoryItem[];
  selectedDate?: string | null;
  onChange?: (scheduledDate: string | null) => void;
};

const LATEST_ID = "0";

/** Returns the CSS variable string for a score's bar color (for SVG/background use). */
const getScoreColorCssVar = (v: number | null): string => {
  if (!v) return "var(--team-app-bar-empty)";
  if (v < 2) return "var(--team-app-score-critical)";
  if (v < 4) return "var(--team-app-score-medium)";
  return "var(--team-app-score-high)";
};

const ScoreCircle = ({
  score,
  completed,
}: {
  score: number | null;
  completed: boolean;
}) => {
  if (!completed) {
    return (
      <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
        <svg width="10" height="10" viewBox="0 0 10 10">
          <circle
            cx="5"
            cy="5"
            r="4.25"
            fill="none"
            stroke="var(--team-app-text-secondary)"
            strokeWidth="1.5"
          />
          <line
            x1="2.5"
            y1="5"
            x2="7.5"
            y2="5"
            stroke="var(--team-app-text-secondary)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: getScoreColorCssVar(score),
        flexShrink: 0,
      }}
    />
  );
};

export const SelectCheckInDateField = ({
  checkInHistory,
  selectedDate,
  onChange,
}: Readonly<SelectCheckInDateFieldProps>) => {
  const [value, setValue] = useState(selectedDate ?? LATEST_ID);

  useEffect(() => {
    setValue(selectedDate ?? LATEST_ID);
  }, [selectedDate]);

  const sorted = checkInHistory
    ? [...checkInHistory]
        .filter((ch) => !!ch.scheduledDate)
        .sort(
          (a, b) =>
            new Date(b.scheduledDate).getTime() -
            new Date(a.scheduledDate).getTime()
        )
    : [];

  // Drop the first (most recent) since it's represented by "Latest Check In"
  const previousItems = sorted.slice(1);

  const options = [
    {
      id: LATEST_ID,
      name: LATEST_ID,
      displayName: "Latest Check In",
    },
    ...previousItems.map((item) => ({
      id: item.scheduledDate,
      name: item.scheduledDate,
      displayName: new Date(item.scheduledDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      startIcon: (
        <ScoreCircle
          score={item.overallScore}
          completed={item.completedDate !== null}
        />
      ),
    })),
  ];

  const handleChange = (e: { target: { value: unknown } }) => {
    const selected = e.target.value as string;
    setValue(selected);
    onChange?.(selected === LATEST_ID ? null : selected);
  };

  return (
    <Box>
      <StyledDropDown
        startAdornment={<SmallCustomIcon icon="CALENDAR" />}
        value={value}
        onChange={handleChange}
        options={options}
      />
    </Box>
  );
};
